import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, HelpCircle, Send } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export default function AskSakhis() {
  const { user } = useAuth();
  const [qs, setQs] = useState(null);
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => API.get("/ask").then((r) => setQs(r.data)).catch(() => setQs([]));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!user) { toast.error("Log in to ask the Sakhis 💗"); return; }
    if (!body.trim()) { toast.error("Write your question"); return; }
    setBusy(true);
    try { await API.post("/ask", { body, anonymous: anon }); setBody(""); setAnon(false); toast.success("Posted! The Sakhis will reply 💗"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#FAF6F1] min-h-[70vh]" data-testid="ask-sakhis-page">
      <Seo title="Ask Sakhis | Suvikka" description="Ask the Suvikka community for style, beauty and jewellery advice — real answers from real Sakhis." canonicalPath="/ask-sakhis" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">Suvikka Sakhi</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">Ask Sakhis 💬</h1>
        <p className="text-stone-500 mt-2">Need advice on a look, shade or style? The community's got you.</p>

        <div className="bg-white border border-stone-200 rounded-2xl p-5 mt-6">
          <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What do you want to ask the Sakhis?" className="w-full border border-stone-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#6A1B29]" data-testid="ask-input" />
          <div className="flex items-center justify-between mt-3">
            <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} data-testid="ask-anon" /> Ask anonymously</label>
            <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-2.5 rounded-full text-sm disabled:opacity-60" data-testid="ask-submit"><Send className="w-4 h-4" /> Ask</button>
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {qs === null ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-stone-100 animate-pulse" />)
            : qs.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="ask-empty">
                <HelpCircle className="w-8 h-8 text-[#6A1B29] mx-auto" />
                <p className="font-display text-xl text-stone-800 mt-3">Be the first to ask ✨</p>
              </div>
            ) : qs.map((q) => (
              <Link key={q.id} to={`/ask-sakhis/${q.id}`} className="block bg-white border border-stone-200 rounded-2xl p-5 hover:border-[#6A1B29] transition-colors" data-testid={`question-${q.id}`}>
                <p className="text-stone-800 font-medium">{q.body}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                  <span>{q.user_name}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {q.answer_count} answers</span>
                </div>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}
